import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { useToast } from '@/components/ui/use-toast';

// Extended to 4 hours to support long voice sessions and consultations
const INACTIVITY_TIMEOUT = 4 * 60 * 60 * 1000; // 4 hours in milliseconds

export const useAutoLogout = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (user) {
      timeoutRef.current = setTimeout(async () => {
        console.log('[AutoLogout] Sessão expirada por inatividade');
        toast({
          title: 'Sessão expirada',
          description: 'Você foi desconectado por inatividade. Faça login novamente.',
          variant: 'default',
        });
        await signOut();
      }, INACTIVITY_TIMEOUT);
    }
  }, [user, signOut, toast]);

  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    const handleActivity = () => {
      resetTimer();
    };

    // Listen for custom activity event from voice/hands-free features
    const handleCustomActivity = () => {
      console.log('[AutoLogout] Custom activity event received (voice/hands-free)');
      resetTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Listen for custom ai4me:activity event (dispatched by voice features)
    window.addEventListener('ai4me:activity', handleCustomActivity);

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('ai4me:activity', handleCustomActivity);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user, resetTimer]);

  return {
    resetTimer,
    lastActivity: lastActivityRef.current
  };
};
